import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { ApiGet } from '../../../../ApiServices/ApiServices';
import { toast } from 'react-toastify';

const DiningTables = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);

  // Status mapping: 1 = Available, 2 = Occupied, 3 = Reserved
  const statusConfig = {
    1: { text: 'Available', color: '#10b981', bgColor: '#ecfdf5', borderColor: '#a7f3d0' },
    2: { text: 'Occupied', color: '#ef4444', bgColor: '#fef2f2', borderColor: '#fecaca' },
    3: { text: 'Reserved', color: '#f59e0b', bgColor: '#fffbeb', borderColor: '#fde68a' }
  };

  const getStatusConfig = (status) => statusConfig[status] || statusConfig[1];

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    setLoading(true);
    try {
      const result = await ApiGet('/api/cashier/dining_tables/filter', {
        pageNumber: 0,
        pageSize: 100
      });
      if (result.success) {
        const data = result.success.data?.data || result.success.data || {};
        setTables(data.records || []);
      }
    } catch (err) {
      toast.error('Failed to fetch dining tables');
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = '#d74440';

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <h4 style={{
          color: primaryColor,
          fontWeight: '700',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <i className="bi bi-grid-3x3-gap-fill"></i>
          Dining Tables
        </h4>

        {/* Status Summary */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {[
            { status: 1, label: 'Available' },
            { status: 2, label: 'Occupied' },
            { status: 3, label: 'Reserved' }
          ].map(item => {
            const config = getStatusConfig(item.status);
            const count = tables.filter(t => t.status === item.status).length;
            return (
              <div
                key={item.status}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  background: config.bgColor,
                  border: `1px solid ${config.borderColor}`
                }}
              >
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: config.color
                }}></div>
                <span style={{ fontSize: '13px', fontWeight: '600', color: config.color }}>
                  {item.label}
                </span>
                <span style={{
                  background: config.color,
                  color: '#fff',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: '700'
                }}>
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tables Grid */}
      {loading ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 20px',
          color: '#999'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid #f3f3f3',
            borderTop: `3px solid ${primaryColor}`,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          <p style={{ marginTop: '16px', fontSize: '14px' }}>Loading dining tables...</p>
        </div>
      ) : tables.length > 0 ? (
        <Row className="g-4">
          {tables.map((table) => {
            const config = getStatusConfig(table.status);
            return (
              <Col key={table.id} xs={6} sm={4} md={3} lg={2}>
                <div
                  style={{
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '20px 16px',
                    textAlign: 'center',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    border: `2px solid ${config.borderColor}`,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                  }}
                >
                  {/* Status indicator bar */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: config.color
                  }}></div>

                  {/* Table Icon */}
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: config.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px'
                  }}>
                    <i className="bi bi-grid-3x3" style={{ fontSize: '24px', color: config.color }}></i>
                  </div>

                  {/* Table Number */}
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#333',
                    marginBottom: '4px'
                  }}>
                    {table.tableNumber || `T${table.id}`}
                  </div>

                  {/* Capacity */}
                  <div style={{
                    fontSize: '13px',
                    color: '#888',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}>
                    <i className="bi bi-people-fill" style={{ fontSize: '12px' }}></i>
                    {table.capacity || 4} Seats
                  </div>

                  {/* Status Badge */}
                  <div style={{
                    display: 'inline-block',
                    padding: '6px 14px',
                    borderRadius: '20px',
                    background: config.bgColor,
                    border: `1px solid ${config.borderColor}`,
                    fontSize: '12px',
                    fontWeight: '600',
                    color: config.color,
                    marginBottom: '10px'
                  }}>
                    {config.text}
                  </div>

                  {/* Section Type */}
                  {table.sectionId?.type && (
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}>
                      <i className="bi bi-tag-fill" style={{ fontSize: '10px', color: primaryColor }}></i>
                      {table.sectionId.type}
                    </div>
                  )}

                  {/* Notes */}
                  {table.notes && (
                    <div style={{
                      fontSize: '11px',
                      color: '#999',
                      marginTop: '6px',
                      fontStyle: 'italic'
                    }}>
                      {table.notes}
                    </div>
                  )}
                </div>
              </Col>
            );
          })}
        </Row>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '80px 20px',
          color: '#999'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <i className="bi bi-grid-3x3" style={{ fontSize: '32px', color: '#ccc' }}></i>
          </div>
          <p style={{ fontSize: '16px', fontWeight: '500', color: '#666', margin: 0 }}>
            No dining tables found
          </p>
          <p style={{ fontSize: '13px', color: '#999', marginTop: '4px' }}>
            Tables will appear here once added
          </p>
        </div>
      )}

      {/* Total Count */}
      {tables.length > 0 && (
        <div style={{
          textAlign: 'center',
          marginTop: '24px',
          padding: '12px',
          background: '#f8f9fa',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#666'
        }}>
          Total <strong>{tables.length}</strong> dining tables
        </div>
      )}
    </Container>
  );
};

export default DiningTables;
